import { Analytics } from '@cobliteam/analytics';
import { type Media, MediaType } from '@cobliteam/common-types';
import Icon from '@cobliteam/ui/icons';
import { AdvancedMarker, AdvancedMarkerAnchorPoint, useAdvancedMarkerRef, useMap } from '@vis.gl/react-google-maps';
import { useEffect } from 'react';
import { getMediaEventLabel } from '../../../../components/MediaTags';
import useMediaStore from '../../../../store/useMediaStore';
import MediaTrackingPopup from '../../../MediaTrackingPopup';
import { isMediaEvent } from '../../utils';

interface MapEventMarkerProps {
  media: Media;
}

/**
 * Renders a map marker for event-type media (event and footage)
 * Handles click interactions and position centering when active
 */
const MapEventMarker = ({ media }: MapEventMarkerProps) => {
  // Get necessary references and state
  const [markerRef] = useAdvancedMarkerRef();
  const map = useMap();
  const { id, mediaDetails, mediaType } = media;

  // Access media store state
  const [activeMarkerId, setActiveMarkerId] = useMediaStore((state) => [state.activeMarkerId, state.setActiveMarkerId]);

  // Determine if this media item is an event-type video
  const isEvent = mediaType === MediaType.EVENT_VIDEO && isMediaEvent(mediaDetails);

  // Handle marker click to toggle active state
  const handleMarkerClick = () => {
    Analytics.track('MediaRequestDialog - Click - Map Event Marker');

    setActiveMarkerId(activeMarkerId === id ? null : id);
  };

  // Center map on marker when it becomes active
  useEffect(() => {
    if (map && activeMarkerId === id && isEvent) {
      // Offset the center point slightly south to account for the popup
      const offset = 0.002;

      map.panTo({
        lat: mediaDetails.latitude + offset,
        lng: mediaDetails.longitude,
      });
      map.setZoom(15);
    }
  }, [activeMarkerId, id, map, mediaDetails, isEvent]);

  // Don't render non-event media
  if (!isEvent) return null;

  const position = {
    lat: mediaDetails.latitude,
    lng: mediaDetails.longitude,
  };

  // Render the event marker
  return (
    <AdvancedMarker key={id} ref={markerRef} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} position={position} zIndex={10} className='relative' onClick={handleMarkerClick}>
      <Icon name='video' color='white' className='box-content rounded-full border border-white bg-danger fill-white p-1 shadow-md' />
      {activeMarkerId === id && (
        <div className='-top-2 -translate-x-1/2 -translate-y-full absolute left-1/2 z-50 transform rounded-md bg-white px-2.5 py-1 text-black shadow-md'>
          <MediaTrackingPopup title={getMediaEventLabel(mediaDetails.eventTrigger)} timestamp={media.timestamp} position={position} severity={media.severity} />
        </div>
      )}
    </AdvancedMarker>
  );
};

export default MapEventMarker;
